import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { childAuthService, type ChildData, type ChildPermissions, type TaskWithStatus, type RewardWithProgress } from '@/services/child-auth.service'
import { childPinService } from '@/services/child-pin.service'
import { useAppContextStore } from '@/stores/appContext'
import { supabase, supabaseRpc } from '@/lib/supabase'
import type { Household } from '@/models/Household'

const SESSION_EXPIRY_KEY = 'harmonia_child_last_opened'
const SESSION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

export const useChildAuthStore = defineStore('childAuth', () => {
  // State
  const child = ref<ChildData | null>(null)
  const isAuthenticated = ref(false)
  const loading = ref(true)
  const error = ref<string | null>(null)

  // Tasks state
  const tasks = ref<TaskWithStatus[]>([])
  const tasksLoading = ref(false)

  // Rewards state
  const rewards = ref<RewardWithProgress[]>([])
  const rewardsLoading = ref(false)
  const collectedRewards = ref<unknown[]>([])

  // Computed
  const permissions = computed<ChildPermissions | null>(() => child.value?.permissions || null)
  const childName = computed(() => child.value?.name || '')
  const childId = computed(() => child.value?.id || '')
  const householdId = computed(() => child.value?.householdId || '')
  const points = computed(() => child.value?.points || 0)
  const currentLevel = computed(() => child.value?.currentLevel || 1)
  const avatar = computed(() => child.value?.avatar || null)

  // Computed for tasks
  const todayTasks = computed(() => tasks.value.filter(t => !t.isCompleted))
  const completedTasks = computed(() => tasks.value.filter(t => t.isCompleted))
  const pendingSubmissions = computed(() => tasks.value.filter(t => t.submissionStatus === 'pending'))
  const tasksProgress = computed(() => {
    if (tasks.value.length === 0) return 0
    return Math.round((completedTasks.value.length / tasks.value.length) * 100)
  })

  function checkSessionExpiry(): boolean {
    const lastOpened = localStorage.getItem(SESSION_EXPIRY_KEY)
    if (!lastOpened) return false
    const elapsed = Date.now() - parseInt(lastOpened, 10)
    return elapsed > SESSION_MAX_AGE_MS
  }

  function touchSession(): void {
    localStorage.setItem(SESSION_EXPIRY_KEY, Date.now().toString())
  }

  /**
   * Set up appContext for child-mode views.
   * Children are not in household_members, so we fetch the household directly
   * and inject it into appContext instead of using ensureHouseholdsLoaded.
   */
  async function setupChildAppContext(childData: ChildData): Promise<void> {
    try {
      const appContextStore = useAppContextStore()

      // Fetch household directly (child is not in household_members)
      const { data: household } = await supabase
        .from('households')
        .select('*')
        .eq('id', childData.householdId)
        .single()

      // Fetch children of this household directly (bypass RPC that checks household_members)
      const { data: children } = await supabase
        .from('children')
        .select('*')
        .eq('household_id', childData.householdId)

      if (household) {
        // Set Pinia state directly WITHOUT localStorage persistence.
        // preloadContext writes to localStorage which would corrupt the parent's
        // session if parent and child are tested in the same browser.
        appContextStore.currentHousehold = household as unknown as Household
        appContextStore.availableHouseholds = [household as unknown as Household]
        appContextStore.availableChildren = (children || []) as any[]
        appContextStore.currentChild = (children || []).find((c: any) => c.id === childData.id) as any || null
      }

      appContextStore.enterChildMode(childData.id)

      // Start realtime sync so the child receives live updates
      // (e.g. parent validates a task → child sees it immediately)
      if (household) {
        appContextStore.startAllRealtimeSync(household.id as string)
      }
    } catch (err) {
      console.error('[ChildAuth] Failed to set up app context:', err)
    }
  }

  async function initSession(): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      if (checkSessionExpiry()) {
        console.log('[ChildAuth] Session expired (30 days inactive)')
        await logout()
        return false
      }

      const { data: { session }, error: sessionError } = await supabase.auth.getSession()

      if (sessionError || !session) {
        isAuthenticated.value = false
        loading.value = false
        return false
      }

      // Un compte enfant n'a pas d'e-mail (anciens comptes anonymes) ou un
      // e-mail technique <child_id>@child.harmonia.local. Un parent connecté
      // avec son vrai e-mail n'est jamais un enfant : inutile de payer, à
      // chaque navigation, un getUser() réseau et une lecture de children qui
      // bloquent le routeur au démarrage (HARMONIA-76).
      const email = session.user?.email
      if (email && !email.toLowerCase().endsWith('@child.harmonia.local')) {
        isAuthenticated.value = false
        loading.value = false
        return false
      }

      const childData = await childAuthService.getChildData()

      if (childData) {
        child.value = childData
        isAuthenticated.value = true
        touchSession()

        // Set up appContext so child-mode views have household + child data
        await setupChildAppContext(childData)

        // L'espace de l'enfant demande le profil ouvert ET la fonctionnalité :
        // gratuit avec le profil dans un foyer `child_limit`, derrière
        // l'abonnement dans un foyer `legacy`, comme il l'a toujours été pour
        // lui. `child_has_full_access` ne conviendrait PAS ici : il répond
        // toujours oui à un foyer `legacy` et laisserait ouverte une session
        // qu'un abonnement expiré fermait avant. Le serveur refuse déjà toute NOUVELLE connexion
        // (verify_child_pin), mais une session ouverte se rafraîchit toute
        // seule : c'est ici, au seul point d'entrée, qu'on la referme.
        //
        // La question part au SERVEUR et n'est pas recalculée ici : dans une
        // session enfant, la liste des frères et sœurs dépend d'une RLS à part,
        // et un enfant seul dans une liste tronquée passerait à tort pour
        // l'unique enfant gratuit du foyer.
        //
        // Une réponse absente (réseau, RLS) ne déconnecte personne : punir une
        // panne serait pire que laisser filer une session, le serveur restant
        // l'autorité sur chaque écriture.
        const { data: hasAccess, error: accessError } =
          await supabaseRpc.rpc('child_feature_allowed', { p_child_id: childData.id })
        if (!accessError && hasAccess === false) {
          console.log('[ChildAuth] Child profile is locked, ending child session')
          await logout()
          error.value = 'PREMIUM_REQUIRED'
          return false
        }

        return true
      } else {
        isAuthenticated.value = false
        return false
      }
    } catch (err: any) {
      console.error('[ChildAuth] Session initialization error:', err)
      error.value = err.message
      isAuthenticated.value = false
      return false
    } finally {
      loading.value = false
    }
  }

  async function loginWithPin(pin: string): Promise<boolean> {
    error.value = null
    loading.value = true

    try {
      const credentials = await childPinService.verifyPin(pin)
      const { userId } = await childPinService.signInWithCredentials(credentials.email, credentials.password, credentials.isNewUser)

      // Lien enfant <-> compte, nécessaire à la RLS et à getChildData.
      //
      // Posé À CHAQUE connexion, plus seulement à la première : `isNewUser`
      // n'est vrai qu'une fois dans la vie d'un enfant, donc s'y fier rendait
      // la panne définitive dès que la toute première tentative s'interrompait
      // entre la création du compte et la pose du lien. getChildData ne
      // trouvait alors plus rien, et l'écran annonçait « code invalide » sur
      // un PIN parfaitement valide, sans aucun moyen d'en sortir. La RPC est
      // idempotente : la rappeler ne fait que rafraîchir un lien déjà correct.
      await childAuthService.linkChildToAccount(credentials.childId, userId)

      const childData = await childAuthService.getChildData()

      if (!childData) {
        error.value = 'Failed to load child data'
        return false
      }

      child.value = childData
      isAuthenticated.value = true
      touchSession()

      // Set up appContext so child-mode views can access household + child data
      await setupChildAppContext(childData)

      return true
    } catch (err: any) {
      console.error('[ChildAuth] PIN login error:', err)
      if (err.message === 'TOO_MANY_ATTEMPTS') {
        error.value = 'TOO_MANY_ATTEMPTS'
      } else if (err.message === 'INVALID_PIN') {
        error.value = 'INVALID_PIN'
      } else if (err.message === 'PREMIUM_REQUIRED') {
        error.value = 'PREMIUM_REQUIRED'
      } else if (err.message === 'CHILD_ACCOUNT_NOT_CONFIRMED') {
        // Le PIN était bon : ne jamais retomber sur « code invalide », qui
        // enverrait l'enfant redemander un code déjà valide.
        error.value = 'CHILD_ACCOUNT_NOT_CONFIRMED'
      } else {
        error.value = err.message || 'Failed to login'
      }
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await supabase.auth.signOut()
    } catch (err) {
      console.error('[ChildAuth] Logout error:', err)
    }

    child.value = null
    isAuthenticated.value = false
    tasks.value = []
    rewards.value = []
    collectedRewards.value = []
    localStorage.removeItem(SESSION_EXPIRY_KEY)

    // Exit child-view in appContext so a subsequent parent session doesn't
    // inherit this child's context (would mis-resolve the coins balance alias).
    try {
      const { useAppContextStore } = await import('@/stores/appContext')
      useAppContextStore().exitChildMode()
    } catch (e) {
      console.error('[ChildAuth] Failed to exit child mode on logout:', e)
    }
  }

  async function loadTasks(date?: string): Promise<void> {
    if (!isAuthenticated.value) return

    tasksLoading.value = true

    try {
      const response = await childAuthService.getTasks(date)
      tasks.value = response.tasks
    } catch (err: any) {
      console.error('[ChildAuth] Load tasks error:', err)
      error.value = err.message
    } finally {
      tasksLoading.value = false
    }
  }

  async function submitTask(taskId: string, date?: string): Promise<boolean> {
    if (!isAuthenticated.value) return false

    try {
      const response = await childAuthService.submitTask(taskId, date)

      if (response.success) {
        const taskIndex = tasks.value.findIndex(t => t.id === taskId)
        if (taskIndex !== -1) {
          tasks.value[taskIndex] = {
            ...tasks.value[taskIndex],
            submissionStatus: 'pending',
            submissionId: response.submissionId
          }
        }
        return true
      }

      return false
    } catch (err: any) {
      error.value = err.message
      return false
    }
  }

  async function cancelTaskSubmission(submissionId: string): Promise<boolean> {
    if (!isAuthenticated.value) return false

    try {
      const response = await childAuthService.cancelTaskSubmission(submissionId)

      if (response.success) {
        await loadTasks()
        return true
      }

      return false
    } catch (err: any) {
      console.error('[ChildAuth] Cancel error:', err)
      error.value = err.message
      return false
    }
  }

  async function loadRewards(): Promise<void> {
    if (!isAuthenticated.value) return

    rewardsLoading.value = true

    try {
      const response = await childAuthService.getRewards()
      rewards.value = response.rewards
      collectedRewards.value = response.collectedRewards

      if (child.value) {
        child.value = {
          ...child.value,
          points: response.currentPoints
        }
      }
    } catch (err: any) {
      console.error('[ChildAuth] Load rewards error:', err)
      error.value = err.message
    } finally {
      rewardsLoading.value = false
    }
  }

  async function updateAvatar(avatarId?: string, avatarConfig?: Record<string, unknown>): Promise<boolean> {
    if (!isAuthenticated.value) return false

    try {
      const response = await childAuthService.updateAvatar(avatarId, avatarConfig)

      if (response.success && avatarId && child.value) {
        child.value = {
          ...child.value,
          avatar: avatarId
        }
      }

      return response.success
    } catch (err: any) {
      error.value = err.message
      return false
    }
  }

  async function refreshChildData(): Promise<void> {
    if (!isAuthenticated.value) return

    try {
      const childData = await childAuthService.getChildData()
      if (childData) {
        child.value = childData
      }
    } catch (err: any) {
      console.error('[ChildAuth] Refresh error:', err)
    }
  }

  function hasPermission(permission: keyof ChildPermissions): boolean {
    return permissions.value?.[permission] ?? false
  }

  function clearError(): void {
    error.value = null
  }

  return {
    // State
    child,
    isAuthenticated,
    loading,
    error,
    tasks,
    tasksLoading,
    rewards,
    rewardsLoading,
    collectedRewards,

    // Computed
    permissions,
    childName,
    childId,
    householdId,
    points,
    currentLevel,
    avatar,
    todayTasks,
    completedTasks,
    pendingSubmissions,
    tasksProgress,

    // Actions
    initSession,
    loginWithPin,
    logout,
    clearError,
    loadTasks,
    submitTask,
    cancelTaskSubmission,
    loadRewards,
    updateAvatar,
    refreshChildData,
    hasPermission
  }
})
